/**
 * TranscriptionService — Provides speech-to-text via Web Speech API.
 *
 * Primary: Web Speech API (SpeechRecognition) — free, real-time, Chrome/Edge.
 * Fallback: Whisper WASM — deferred to P1 (stub provided).
 *
 * The service provides both live interim transcription and final results.
 */

/** Callback types for transcription events */
export interface TranscriptionCallbacks {
  /** Called with interim (partial) transcription text */
  onInterim?: (text: string) => void;
  /** Called when a final transcript segment is produced */
  onFinal?: (text: string) => void;
  /** Called on error */
  onError?: (error: string) => void;
}

/**
 * Minimal type declarations for the Web Speech API.
 * These types are not part of TypeScript's DOM lib (Chrome/Edge only).
 */
interface WebSpeechRecognitionEvent extends Event {
  readonly resultIndex: number;
  readonly results: {
    readonly length: number;
    [index: number]: {
      readonly isFinal: boolean;
      readonly length: number;
      [index: number]: {
        readonly transcript: string;
        readonly confidence: number;
      };
    };
  };
}

interface WebSpeechRecognitionErrorEvent extends Event {
  readonly error: string;
  readonly message: string;
}

interface WebSpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: WebSpeechRecognitionEvent) => void) | null;
  onerror: ((event: WebSpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

interface WebSpeechRecognitionConstructor {
  new (): WebSpeechRecognition;
}

interface SpeechRecognitionWindow extends Window {
  SpeechRecognition?: WebSpeechRecognitionConstructor;
  webkitSpeechRecognition?: WebSpeechRecognitionConstructor;
}

export class TranscriptionService {
  private recognition: WebSpeechRecognition | null = null;
  private finalTranscript: string = "";
  private callbacks: TranscriptionCallbacks = {};

  /** Check if Web Speech API is available */
  static isWebSpeechAvailable(): boolean {
    const win = window as unknown as SpeechRecognitionWindow;
    return !!(win.SpeechRecognition || win.webkitSpeechRecognition);
  }

  /** Check if any transcription method is available */
  static isAvailable(): boolean {
    return TranscriptionService.isWebSpeechAvailable();
    // Future: || WhisperWasm.isAvailable()
  }

  /** Get the transcription engine name for UI display */
  static getEngineName(): string {
    if (TranscriptionService.isWebSpeechAvailable()) {
      return "Web Speech API";
    }
    return "None (manual transcription only)";
  }

  /** Start live transcription */
  startLiveTranscription(callbacks: TranscriptionCallbacks): void {
    if (!TranscriptionService.isWebSpeechAvailable()) {
      callbacks.onError?.("Speech recognition not available in this browser.");
      return;
    }

    this.callbacks = callbacks;
    this.finalTranscript = "";

    const win = window as unknown as SpeechRecognitionWindow;
    const SpeechRecognitionCtor =
      win.SpeechRecognition ?? win.webkitSpeechRecognition;

    if (!SpeechRecognitionCtor) {
      callbacks.onError?.("Speech recognition constructor not found.");
      return;
    }

    this.recognition = new SpeechRecognitionCtor();
    this.recognition.continuous = true;
    this.recognition.interimResults = true;
    this.recognition.lang = "en-CA";
    (
      this.recognition as unknown as { maxAlternatives: number }
    ).maxAlternatives = 3;

    this.recognition.onresult = (event: WebSpeechRecognitionEvent) => {
      let interim = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const transcript = result[0].transcript;

        if (result.isFinal) {
          this.finalTranscript += transcript;
          this.callbacks.onFinal?.(transcript);
        } else {
          interim += transcript;
        }
      }

      if (interim) {
        this.callbacks.onInterim?.(this.finalTranscript + interim);
      }
    };

    this.recognition.onerror = (event: WebSpeechRecognitionErrorEvent) => {
      // 'no-speech' and 'aborted' are not real errors
      if (event.error !== "no-speech" && event.error !== "aborted") {
        this.callbacks.onError?.(`Speech recognition error: ${event.error}`);
      }
    };

    // Auto-restart if it stops unexpectedly (Chrome can stop after silence)
    this.recognition.onend = () => {
      // If we still have a reference, it wasn't intentionally stopped
      if (this.recognition) {
        try {
          this.recognition.start();
        } catch {
          // Browser throws if recognition was already stopped/aborted between
          // the onend event firing and this handler executing — safe to ignore
        }
      }
    };

    this.recognition.start();
  }

  /** Stop live transcription and return final text */
  stopLiveTranscription(): string {
    const recognition = this.recognition;
    this.recognition = null; // Clear ref first to prevent auto-restart

    if (recognition) {
      try {
        recognition.stop();
      } catch {
        // Browser throws if recognition already stopped between clearing the
        // ref above and this call — safe to ignore
      }
    }

    const result = this.finalTranscript.trim();
    this.finalTranscript = "";
    this.callbacks = {};
    return result;
  }

  /** Get current accumulated transcript */
  getCurrentTranscript(): string {
    return this.finalTranscript;
  }
}
